import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, CheckCircle2, Loader2 } from 'lucide-react';
import PhoneField, { isPhoneValid } from './PhoneField';
import VatNotice from './VatNotice';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Price-request lead capture modal.
 * Pre-fills the artwork title + artist into the message and posts to /api/contact
 * with inquiry_type='price_request'. Uses the existing AI-triaged contact pipeline.
 */
const PriceRequestModal = ({ open, onClose, artwork, selectedFormat = null, intent = 'price', prefillNote = '' }) => {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [note, setNote] = useState(prefillNote);
  const [subscribe, setSubscribe] = useState(true);
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');

  // Keep the note in sync if the parent changes its prefill (e.g. when the
  // visitor picks a different frame colour and re-opens the modal).
  React.useEffect(() => {
    if (open) setNote(prefillNote);
  }, [open, prefillNote]);

  const reset = () => {
    setName(''); setEmail(''); setPhone(''); setNote(prefillNote);
    setSubscribe(true);
    setSubmitted(false); setError(''); setLoading(false);
  };

  const handleClose = () => { reset(); onClose(); };

  const hasPrice = Boolean(artwork?.priceDisplay);
  const isVideo = intent === 'video';

  const copy = isVideo
    ? {
        eyebrow: 'Request a video',
        subtitle: 'A specialist will film a short walk-around video of this work and send it to you within 24 hours.',
        submit: 'Request video',
        successHeadline: 'Video request received',
        successBody:
          'A specialist will film a short walk-around video of the sculpture and send it to you within 24 hours.',
      }
    : {
        eyebrow: hasPrice ? 'Enquire about this work' : 'Request price',
        subtitle: null,
        submit: hasPrice ? 'Send enquiry' : 'Request price',
        successHeadline: 'Request received',
        successBody:
          'A specialist will be in touch within 24 hours with pricing and provenance details.',
      };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!isPhoneValid(phone)) {
      setError('Please enter a valid phone number with country code.');
      return;
    }
    setLoading(true);
    setError('');
    try {
      const formatLabel = selectedFormat
        ? `${selectedFormat.size}${selectedFormat.priceDisplay ? ` (${selectedFormat.priceDisplay})` : ''}`
        : null;
      const headlinePrice = selectedFormat?.priceDisplay || artwork?.priceDisplay;
      let priceContext;
      if (isVideo) {
        priceContext = `Video request for "${artwork.title}" by ${artwork.artistName}`;
      } else if (headlinePrice) {
        priceContext = `Enquiry about "${artwork.title}" by ${artwork.artistName} (${formatLabel || `list price ${headlinePrice}`})`;
      } else {
        priceContext = `Price enquiry for "${artwork.title}" by ${artwork.artistName}`;
      }
      const message =
        priceContext +
        `${artwork.year ? ` (${artwork.year})` : ''}` +
        `${artwork.edition ? `, ${artwork.edition}` : ''}` +
        `${selectedFormat?.sku ? ` — SKU ${selectedFormat.sku}` : ''}` +
        `${artwork.id ? ` — artwork ID ${artwork.id}` : ''}.` +
        (phone ? ` Phone: ${phone}.` : '') +
        (note ? ` Notes: ${note}` : '');
      const res = await fetch(`${API_URL}/api/contact`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name,
          email,
          phone: phone || null,
          message,
          inquiry_type: isVideo ? 'video_request' : 'price_request',
          artwork_title: artwork?.title || null,
          artwork_id: artwork?.id || null,
          artwork_slug: artwork?.slug || null,
          artwork_image_url: artwork?.imageUrl || null,
          artwork_artist: artwork?.artistName || null,
          artwork_year: artwork?.year || null,
          artwork_medium: artwork?.medium || null,
          artwork_dimensions: artwork?.dimensions || null,
          artwork_edition: artwork?.edition || null,
          artwork_signature: artwork?.signature || (artwork?.edition && /signed/i.test(artwork?.edition || '') ? 'Hand signed' : null),
          format_size: selectedFormat?.size || null,
          format_sku: selectedFormat?.sku || null,
          format_price: selectedFormat?.price ?? null,
          format_currency: selectedFormat?.currency || artwork?.currency || null,
          subscribe: subscribe,
        }),
      });
      if (!res.ok) {
        const j = await res.json().catch(() => ({}));
        throw new Error(j.detail || `Request failed (${res.status})`);
      }
      setSubmitted(true);
    } catch (err) {
      setError(err.message || 'Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <AnimatePresence>
      {open && (
        <>
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/60 z-50"
            onClick={handleClose}
            data-testid="price-modal-backdrop"
          />
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 20 }}
            transition={{ duration: 0.2 }}
            className="fixed inset-0 z-50 flex items-center justify-center p-4 pointer-events-none"
          >
            <div
              className="bg-white w-full max-w-md rounded-sm shadow-2xl pointer-events-auto"
              data-testid="price-request-modal"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Header */}
              <div className="flex items-start justify-between p-6 pb-0">
                <div>
                  <p className="text-xs uppercase tracking-widest text-neutral-500 mb-2">
                    {copy.eyebrow}
                  </p>
                  <h2 className="text-2xl font-light leading-tight">
                    {artwork?.title}
                  </h2>
                  <p className="text-sm text-neutral-500 mt-1">by {artwork?.artistName}</p>
                  {copy.subtitle && (
                    <p className="text-sm text-neutral-600 mt-3 leading-relaxed">{copy.subtitle}</p>
                  )}
                  {selectedFormat && (
                    <div
                      className="mt-3 inline-flex items-center gap-2 px-3 py-1.5 bg-neutral-100 rounded-sm text-sm"
                      data-testid="price-modal-selected-format"
                    >
                      <span className="font-medium">{selectedFormat.size}</span>
                      {selectedFormat.priceDisplay && (
                        <>
                          <span className="text-neutral-400">·</span>
                          <span>{selectedFormat.priceDisplay}</span>
                        </>
                      )}
                    </div>
                  )}
                </div>
                <button
                  onClick={handleClose}
                  className="p-2 hover:bg-neutral-100 rounded-full"
                  data-testid="price-modal-close"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              {submitted ? (
                <div className="px-6 pb-8 pt-4 text-center" data-testid="price-modal-success">
                  <CheckCircle2 className="w-14 h-14 text-green-600 mx-auto mb-4" />
                  <p className="text-lg font-light mb-2">{copy.successHeadline}</p>
                  <p className="text-sm text-neutral-600 mb-6">
                    {copy.successBody}
                  </p>
                  <button
                    onClick={handleClose}
                    className="px-8 py-2.5 bg-black text-white hover:bg-neutral-800 transition-colors text-xs uppercase tracking-wider"
                    data-testid="price-modal-done"
                  >
                    Close
                  </button>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="p-6 pt-4 space-y-3">
                  {hasPrice && <VatNotice variant="cart" />}
                  <input
                    type="text"
                    required
                    placeholder="Full name"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    className="w-full px-4 py-3 border border-neutral-300 rounded-sm focus:outline-none focus:border-black text-sm"
                    data-testid="price-modal-name"
                  />
                  <input
                    type="email"
                    required
                    placeholder="Email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="w-full px-4 py-3 border border-neutral-300 rounded-sm focus:outline-none focus:border-black text-sm"
                    data-testid="price-modal-email"
                  />
                  <PhoneField
                    value={phone}
                    onChange={setPhone}
                    required
                    placeholder="Phone number"
                    testIdPrefix="price-modal-phone"
                  />
                  <textarea
                    rows={3}
                    placeholder="Notes or questions (optional)"
                    value={note}
                    onChange={(e) => setNote(e.target.value)}
                    className="w-full px-4 py-3 border border-neutral-300 rounded-sm focus:outline-none focus:border-black text-sm resize-none"
                    data-testid="price-modal-note"
                  />

                  <label className="flex items-start gap-2 cursor-pointer select-none pt-1">
                    <input
                      type="checkbox"
                      checked={subscribe}
                      onChange={(e) => setSubscribe(e.target.checked)}
                      className="mt-0.5 w-4 h-4 accent-black"
                      data-testid="price-modal-subscribe"
                    />
                    <span className="text-xs text-neutral-600 leading-relaxed">
                      Subscribe to our mailing list for new arrivals, private views and exhibition news.
                    </span>
                  </label>

                  {error && (
                    <p className="text-sm text-red-600" data-testid="price-modal-error">{error}</p>
                  )}

                  <button
                    type="submit"
                    disabled={loading}
                    className="w-full px-6 py-3 bg-black text-white hover:bg-neutral-800 disabled:bg-neutral-400 transition-colors text-xs uppercase tracking-wider font-medium flex items-center justify-center gap-2"
                    data-testid="price-modal-submit"
                  >
                    {loading ? (
                      <>
                        <Loader2 className="w-4 h-4 animate-spin" /> Sending…
                      </>
                    ) : (
                      hasPrice ? 'Send Enquiry' : 'Request Price'
                    )}
                  </button>
                  <p
                    className="text-xs text-neutral-500 text-center pt-1"
                    data-testid="price-modal-vat-notice"
                  >
                    VAT &amp; delivery calculated at checkout or upon enquiry.
                  </p>
                  <p className="text-xs text-neutral-500 text-center">
                    By submitting, you agree to be contacted about this artwork.
                  </p>
                </form>
              )}
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

export default PriceRequestModal;
